import React from 'react';
import type { GlobalProps } from '@bloomberg/media-ui-core';
import { classes, useSlug } from '@bloomberg/media-ui-core';

import {
  USER_OPTIONS_MENU_ITEM_SIGN_OUT,
  USER_OPTIONS_MENU_SUBSCRIBE_COPY_TEXT,
} from '../constants/constants';
import { localizeUrl } from '../i18n';
import { useNavContext } from '../Nav/NavContext';
import { THEMES, User, type UserOptionsMenuItems } from '../types';
import {
  TranslatedMessage,
  useTranslateMessage,
} from '../utils/translate-messages';

import styles from './UserOptionsDropdownMobile.module.css';

export type UserOptionsDropdownMobileProps = GlobalProps & {
  sections: UserOptionsMenuItems;
  user: User;
  onChange?: React.FormEvent<HTMLInputElement>;
  onSignOut?: () => void;
  trackingFunction: (action: string, eventLabel: string) => void;
  theme?: THEMES;
  subscriptionUrl: string;
};

type DropdownOptionProps = {
  text: string;
  url: string;
  badge?: string;
  onUserOptionSelect: (value: string) => void;
  onSignOut?: () => void;
};

const SubscribeButton = ({
  trackingFunction,
  subscriptionUrl,
}: {
  trackingFunction: (action: string, eventLabel: string) => void;
  subscriptionUrl: string;
}) => {
  return (
    <a
      className={styles.subscribeButton}
      href={`${subscriptionUrl}?in_source=nav-account-dropdown`}
      target="_blank"
      rel="noreferrer"
      onClick={() => trackingFunction('click', 'subscribe')}
    >
      <TranslatedMessage
        id="media-ui-nav_subscribe"
        defaultMessage="Subscribe"
      />
    </a>
  );
};

const DropdownOption = ({
  text,
  url,
  badge,
  onUserOptionSelect,
  onSignOut,
}: DropdownOptionProps) => {
  if (url === USER_OPTIONS_MENU_ITEM_SIGN_OUT.url) {
    return (
      <div
        className={classes([styles.optionText, styles.signOut])}
        key={text}
        onClick={onSignOut}
        aria-label={`user-dropdown-mobile-option-${text}`}
      >
        <p>{text}</p>
      </div>
    );
  }

  return (
    <div
      className={styles.optionText}
      key={text}
      onClick={() => onUserOptionSelect(url)}
      aria-label={text}
    >
      <p>
        {text}
        {badge && <span className={styles.badge}>{badge}</span>}
      </p>
    </div>
  );
};

export const UserOptionsDropdownMobile = React.forwardRef<
  HTMLDivElement,
  UserOptionsDropdownMobileProps
>(function UserOptionsDropdownMobileComponent(
  props: UserOptionsDropdownMobileProps,
  ref
) {
  const {
    sections,
    theme,
    user,
    onSignOut,
    trackingFunction,
    subscriptionUrl,
  } = props;
  const { locale } = useNavContext();

  const translateMessage = useTranslateMessage();
  const componentName = useSlug('user-options-dropdown-mobile');

  const cx = classes([
    styles.userOptionsDropdownMobileContainer,
    {
      [styles.light]: theme === THEMES.LIGHT,
      [styles.dark]: theme === THEMES.DARK,
      [styles.blue]: theme === THEMES.BLUE,
    },
  ]);

  const onUserOptionSelect = (url: string) => {
    window.open(url, '_self');
  };

  return (
    <div
      className={cx}
      data-component={componentName}
      data-testid="user-options-dropdown"
      ref={ref}
    >
      {!user.isSubscriber && (
        <div className={styles.subscribe}>
          <p className={styles.subscribeText}>
            {translateMessage({
              id: 'media-ui-nav_subscribeToSaveAndGiftArticles',
              defaultMessage: USER_OPTIONS_MENU_SUBSCRIBE_COPY_TEXT,
            })}
          </p>
          <SubscribeButton
            trackingFunction={trackingFunction}
            subscriptionUrl={subscriptionUrl}
          />
        </div>
      )}
      {Object.entries(sections).map(
        ([key, { text: sectionHeaderText, items }], index) => {
          return (
            <>
              <div className={styles.option} key={key}>
                {sectionHeaderText && (
                  <h2 className={styles.sectionHeader}>{sectionHeaderText}</h2>
                )}
                {items.map(({ text, url, badge }) => (
                  <DropdownOption
                    key={text}
                    text={text}
                    badge={badge}
                    url={localizeUrl(locale, url)}
                    onUserOptionSelect={onUserOptionSelect}
                    onSignOut={onSignOut}
                  />
                ))}
              </div>
              {index !== Object.keys(sections).length - 1 && (
                <div className={styles.optionSeparator} />
              )}
            </>
          );
        }
      )}
    </div>
  );
});
